"use client";

import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/shared/ui/components/modal";
import PdfDocument from "@/shared/ui/components/pdf-document";
import {
  getKeyFactsStatementTitleKey,
  selectKeyFactsStatementDocument,
} from "@/lib/checkout-documents";
import * as events from "@/events";
import { emitDocumentTracking } from "@/shared/tracking/analytics";
import type { AppContext, LoanDocument } from "@/types";

interface KeyFactsStatementModalProps {
  ctx: AppContext;
  isOpen: boolean;
  onClose: () => void;
}

export function KeyFactsStatementModal({
  ctx: { t, rtl, session, sendEvent },
  isOpen,
  onClose,
}: KeyFactsStatementModalProps) {
  const { i18n } = useTranslation();
  const hasTrackedThisOpenRef = useRef(false);

  const selectedDocument = useMemo<LoanDocument | null>(
    () => selectKeyFactsStatementDocument(session.documents, i18n.language),
    [session.documents, i18n.language],
  );
  const kfsTitleKey = getKeyFactsStatementTitleKey(session.order.countryCode);

  /**
   * KFS PDF URL is provided on `session.documents[].url` when the session is
   * updated from POST …/select-payment-option (and merged by checkout schema).
   * The separate GET …/documents/:id call is not used for Key Facts — it was
   * made redundant for KFS.
   */
  const documentUrl = selectedDocument
    ? (selectedDocument.url ?? "").trim()
    : "";

  useEffect(() => {
    if (!isOpen) {
      hasTrackedThisOpenRef.current = false;
      return;
    }
    if (!selectedDocument) return;
    if (hasTrackedThisOpenRef.current) return;
    hasTrackedThisOpenRef.current = true;
    sendEvent(events.FINANCE_DOCUMENT_CLICKED, {
      document_id: selectedDocument.id,
      document_type: selectedDocument.documentItemType,
      document_url: selectedDocument.url,
    });
    emitDocumentTracking("finance_document_opened", session, {
      document_id: selectedDocument.id,
      document_type: selectedDocument.documentItemType,
    });
  }, [isOpen, selectedDocument, sendEvent, session]);

  if (!isOpen) return null;

  return (
    <Modal
      onClose={onClose}
      showCloseButton
      title={t(kfsTitleKey)}
    >
      <div
        className="p-6 pt-12 min-h-[900px] flex flex-col"
        dir={rtl ? "rtl" : "ltr"}
        data-testid="key-facts-statement-modal"
      >
        <h3
          className={`text-xl font-semibold text-[#16181D] ${
            rtl ? "text-right" : "text-left"
          }`}
        >
          {t(kfsTitleKey)}
        </h3>

        <div className="mt-4 flex-1 overflow-y-auto overflow-x-hidden">
          {documentUrl ? (
            <PdfDocument url={documentUrl} className="w-full" />
          ) : (
            <div className="flex items-center justify-center min-h-[400px] text-gray-500">
              {t("documents.unavailable")}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
